import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Select } from '@ngxs/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { ToastGlobalService } from '../layout/toast-global/toast-global.service';
import { FeatureSettings, TenantFeatures, isFeatureEnabled } from '../models/featureSettings';
import { Module } from '../models/module';
import { Tenant } from '../models/tenant';
import { NotificationStatus, NotificationType, UserNotification } from '../models/userNotification';
import { AuthService } from '../services/auth.service';
import { TenantState } from '../store/state/tenant.state';
import { TenantSessionState } from '../store/state/tenant-session.state';
import { NotificationsService } from './notifications.service';
import { NotificationsPollingService } from './notifications-polling.service';
import { AppConfigurationService } from '../shared/services/app-configuration.service';
import { Modules } from '../shared/services/spa.routing.service';
import { TranslateService } from '@ngx-translate/core';

interface NotificationTab {
  key: string;
  labelKey: string;
  requiresModule?: string;
}

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.css'],
    standalone: false
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @Select(TenantState.getCurrentTenant) getCurrentTenant: Observable<Tenant>;
  @Select(TenantSessionState.getTenantModules) getTenantModules$: Observable<Module[]>;
  @Select(TenantSessionState.getTenantFeatures) getTenantFeatures$: Observable<FeatureSettings>;

  menuEnabled: boolean = false;
  currentFilter: string = '{}';
  currentSort: string = '{"createDateTimeUtc":-1}';
  currentOffset: number = 0;
  currentLimit: number = 100;
  notifications: UserNotification[] = [];
  userNotifications: UserNotification[] = [];
  count: number = 0;
  NotificationStatus: typeof NotificationStatus = NotificationStatus;
  hideSeen: boolean = true;
  disabled: boolean = false;

  // Fallbacks if app configuration isn't available yet.
  private pollingIntervalSeconds: number = 60;
  private pollingIdleTimeout: number = 300;

  readonly pageSize: number = 10;
  pageIndex: number = 0;
  selectedTabKey: string = 'alert';

  private readonly allTabs: NotificationTab[] = [
    { key: 'alert', labelKey: '@alerts' },
    { key: 'vehicle', labelKey: '@vehicle' },
    { key: 'part', labelKey: '@part' },
    { key: 'work', labelKey: '@work', requiresModule: Modules.WORK },
  ];
  tabs: NotificationTab[] = this.allTabs;

  private destroy$ = new Subject<void>();

  constructor(private service: NotificationsService,
    private pollingService: NotificationsPollingService,
    private authService: AuthService,
    private appConfigurationService: AppConfigurationService,
    private router: Router,
    private toast: ToastGlobalService,
    private translate: TranslateService) { }

  ngOnInit(): void {
    this.appConfigurationService.settings$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(settings => {
        if (settings?.idleTimeoutPeriod) this.pollingIdleTimeout = settings.idleTimeoutPeriod;
      });

    // Start polling only once both the user session and a tenant are present.
    // Re-fetch on every tenant change so switching tenants refreshes the list.
    combineLatest([
      this.authService.user$.pipe(
        filter(u => !!u),
        distinctUntilChanged((a, b) => a.profile?.sub === b.profile?.sub)
      ),
      this.getCurrentTenant.pipe(
        filter(t => !!t),
        distinctUntilChanged((a, b) => a.id === b.id)
      ),
      this.getTenantFeatures$
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([, , features]) => {
        this.menuEnabled = isFeatureEnabled(features, TenantFeatures.ShowNotificationMenu);
        if (this.menuEnabled) {
          this.startPolling();
        } else {
          this.pollingService.cancelPolling();
        }
      });

    this.pollingService.get()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.notifications = res?.documents ?? [];
        this.filterUserNotifications();
      });

    this.getTenantModules$
      .pipe(takeUntil(this.destroy$))
      .subscribe(modules => {
        const modulePaths = new Set((modules ?? []).map(m => m.parentRoutePath));
        this.tabs = this.allTabs.filter(t => !t.requiresModule || modulePaths.has('/' + t.requiresModule));
        if (!this.tabs.find(t => t.key === this.selectedTabKey)) {
          this.selectedTabKey = this.tabs[0]?.key ?? 'alert';
        }
      });
  }

  ngOnDestroy() {
    this.pollingService.cancelPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startPolling(): void {
    // Restart to pick up the current tenant (service tolerates re-entry via its own flag).
    this.pollingService.cancelPolling();
    this.pollingService.beginPolling(
      this.pollingIntervalSeconds,
      this.pollingIdleTimeout,
      this.currentFilter,
      this.currentSort,
      this.currentOffset,
      this.currentLimit
    );
  }

  private get userEmail(): string {
    return this.authService.user?.profile?.email ?? '';
  }

  filterUserNotifications() {
    this.userNotifications = this.notifications.filter(x => {
      return (x.acknowledgedUsers?.indexOf(this.userEmail) ?? -1) < 0;
    });
    this.count = this.userNotifications.length;
  }

  private get visibleNotifications(): UserNotification[] {
    return this.hideSeen ? this.userNotifications : this.notifications;
  }

  // Notifications are categorized into exactly one tab. Schedule-related
  // notifications currently link to vehicle-detail pages, so they're grouped
  // under the Vehicle tab.
  getTabForNotification(n: UserNotification): string | null {
    if (n.type === NotificationType.Alert) return 'alert';
    // Alert-rule backtest completion — alert-rule related, so group under the Alert tab. #11219.
    if (n.type === NotificationType.AlertRuleBacktest) return 'alert';
    if (n.type === NotificationType.PartImport) return 'part';

    const link = (n.link ?? '').toLowerCase();

    if (link.includes('/work/')) return 'work';
    if (n.type === NotificationType.Vehicle ||
        n.type === NotificationType.Sensor ||
        n.type === NotificationType.Group ||
        link.includes('/fleet/') ||
        link.includes('/vehicle')) return 'vehicle';

    return null;
  }

  countForTab(key: string): number {
    return this.visibleNotifications.filter(n => this.getTabForNotification(n) === key).length;
  }

  get tabNotifications(): UserNotification[] {
    return this.visibleNotifications.filter(n => this.getTabForNotification(n) === this.selectedTabKey);
  }

  get pagedNotifications(): UserNotification[] {
    const start = this.pageIndex * this.pageSize;
    return this.tabNotifications.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.tabNotifications.length / this.pageSize));
  }

  selectTab(key: string): void {
    this.selectedTabKey = key;
    this.pageIndex = 0;
  }

  prevPage(): void {
    if (this.pageIndex > 0) this.pageIndex--;
  }

  nextPage(): void {
    if (this.pageIndex < this.totalPages - 1) this.pageIndex++;
  }

  navigate(notification: UserNotification) {
    this.router.navigateByUrl(notification.link);
  }

  dismiss($event: Event, notification: UserNotification) {
    $event.stopPropagation();
    this.userNotifications = this.userNotifications.filter(x => x.id != notification.id);
    this.count = this.userNotifications.length;
    if (this.pageIndex >= this.totalPages) {
      this.pageIndex = Math.max(0, this.totalPages - 1);
    }
    this.service.dismissForUser(notification, this.userEmail).subscribe();
  }

  dismissAll() {
    const dismissAllToast = this.translate.instant('@clearingAllNotifications');
    this.toast.show(dismissAllToast, { classname: 'success' });
    this.disabled = true;
    var dummy = new UserNotification();
    this.service.dismissForUser(dummy, this.userEmail, true).subscribe(
      res => {
        this.disabled = false;
        const toast = this.translate.instant('@updateSuccessful');
        this.toast.show(toast, { classname: 'success' });
        this.userNotifications = [];
        this.count = 0;
        this.hideSeen = true;
        this.pageIndex = 0;
      },
      error => {
        this.disabled = false;
        const toast = this.translate.instant('@updateFailed');
        this.toast.show(toast, { classname: 'error' });
        this.startPolling();
      });
  }

  toggleHideSeen($event: Event) {
    $event.stopPropagation();
    this.hideSeen = !this.hideSeen;
    this.pageIndex = 0;
  }
}
